import { useState, useCallback, ReactElement, useEffect } from 'react';
import { useTracking } from 'react-tracking';

import { TabList, Tab, Box } from '@simplywallst/ui-core';
import type { TabOnClickEvent } from '@simplywallst/ui-core';

import DataTable from '../DataTable';
import TopWorstSkeleton from './TopWorstSkeleton';
import { PeriodTabListWrapper } from './styled';

const periods = ['7D', '1M', '3M', '1Y', '3Y', '5Y'] as const;
export type Period = (typeof periods)[number];
export type Performance = 'top' | 'worst';

interface ChildrenProps {
  tab: Performance;
  period: Period;
}

export interface Props {
  topLabel: string;
  worstLabel: string;
  disabled: boolean;
  children?: (
    props: ChildrenProps
  ) => ReactElement<ChildrenProps>[] | ReactElement<ChildrenProps>;
}

const TopWorst = ({ topLabel, worstLabel, children, disabled }: Props) => {
  const { trackEvent } = useTracking();
  const [activeTab, setActiveTab] = useState<Performance>('top');
  const [activePeriod, setActivePeriod] = useState<Period>('7D');

  const handleTabSelect = useCallback((event: TabOnClickEvent<Performance>) => {
    const value = event.target.value;
    setActiveTab(value);
    trackEvent({
      type: 'track',
      action: 'click',
      modifier: `dashboard-top-worst-tab-${value}`,
    });
  }, []);

  const handlePeriodSelect = useCallback((event: TabOnClickEvent<Period>) => {
    const value = event.target.value;
    setActivePeriod(value);
    trackEvent({
      type: 'track',
      action: 'click',
      modifier: `dashboard-top-worst-tab-${value}`,
    });
  }, []);

  // auto correct activeTab
  useEffect(() => {
    if (activeTab !== 'top' && activeTab !== 'worst') {
      setActiveTab('top');
    }
  }, [activeTab]);

  useEffect(() => {
    if (!periods.includes(activePeriod)) {
      setActivePeriod('7D');
    }
  }, [activePeriod]);

  return (
    <Box marginTop="x2" data-cy-id="dashboard-top-worst-table">
      <TabList templateColumns="max-content max-content">
        <Tab
          selected={activeTab === 'top'}
          onClick={handleTabSelect}
          value="top"
          disabled={disabled}
        >
          {topLabel}
        </Tab>
        <Tab
          selected={activeTab === 'worst'}
          onClick={handleTabSelect}
          value="worst"
          disabled={disabled}
        >
          {worstLabel}
        </Tab>
      </TabList>
      <DataTable
        columnWidths="8, 2"
        columnTextAlignments="l,r"
        disabled={disabled}
      >
        <thead>
          <tr>
            <th colSpan={2}>
              <PeriodTabListWrapper>
                <TabList templateColumns={`repeat(${periods.length}, auto)`}>
                  {periods.map((period) => (
                    <Tab
                      key={period}
                      styleType="period"
                      selected={activePeriod === period}
                      onClick={handlePeriodSelect}
                      value={period}
                      disabled={disabled}
                    >
                      {period}
                    </Tab>
                  ))}
                </TabList>
              </PeriodTabListWrapper>
            </th>
          </tr>
        </thead>
        <tbody>
          {disabled ? (
            <TopWorstSkeleton />
          ) : (
            children &&
            children({
              tab: activeTab,
              period: activePeriod,
            })
          )}
        </tbody>
      </DataTable>
    </Box>
  );
};

export default TopWorst;
